import React, { useState, useEffect } from 'react';
import CitySearch from './CitySearch';
import './HotelSearchForm.css';

/**
 * HotelSearchForm Component
 * 
 * Features:
 * - City autocomplete (like airport search)
 * - Auto-populates from flight data
 * - Warns user if they change city/dates from flight
 */
const HotelSearchForm = ({ onSearch, loading = false, flightData = null }) => {
  const [city, setCity] = useState(null);
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [adults, setAdults] = useState(1);
  const [rooms, setRooms] = useState(1);
  
  // Track if data came from flight auto-population
  const [autoPopulated, setAutoPopulated] = useState(false);

  // Get today's date for min date
  const today = new Date().toISOString().split('T')[0];

  // Auto-populate from flight data
  useEffect(() => {
    if (flightData && !autoPopulated) {
      console.log('Auto-populating hotel form from flight data:', flightData);
      
      // Set city from flight destination
      if (flightData.destination) {
        const cityData = {
          cityCode: flightData.destination,
          cityName: flightData.destinationCity || flightData.destination,
          countryCode: 'US' // Default, will be updated by search
        };
        console.log('Setting city to:', cityData);
        setCity(cityData);
      }
      
      // Set check-in from flight departure date
      if (flightData.departureDate) {
        setCheckIn(flightData.departureDate);
      }
      
      // Set check-out from flight return date
      if (flightData.returnDate) {
        setCheckOut(flightData.returnDate);
      }
      
      // Set adults from flight passengers
      if (flightData.adults) {
        setAdults(flightData.adults);
      }
      
      setAutoPopulated(true);
    }
  }, [flightData, autoPopulated]);

  // Warn user if they change city from flight destination
  const handleCityChange = (newCity) => {
    if (autoPopulated && flightData && flightData.destination) {
      if (newCity && newCity.cityCode !== flightData.destination) {
        const confirmed = window.confirm(
          `⚠️ Warning: You're changing the city!\n\n` +
          `Flight destination: ${flightData.destination}\n` +
          `New hotel city: ${newCity.cityName}\n\n` +
          `Are you sure you want to book a hotel in a different city?`
        );
        
        if (!confirmed) {
          return; // Don't change
        }
      }
    }
    setCity(newCity);
  };

  // Warn user if they change check-in date
  const handleCheckInChange = (newDate) => {
    if (autoPopulated && flightData && flightData.departureDate) {
      if (newDate !== flightData.departureDate) {
        const confirmed = window.confirm(
          `⚠️ Warning: Hotel check-in doesn't match flight arrival!\n\n` +
          `Flight arrival: ${flightData.departureDate}\n` +
          `Hotel check-in: ${newDate}\n\n` +
          `Continue with different dates?`
        );
        
        if (!confirmed) {
          return;
        }
      }
    }
    setCheckIn(newDate);
  };

  // Warn user if they change check-out date
  const handleCheckOutChange = (newDate) => {
    if (autoPopulated && flightData && flightData.returnDate) {
      if (newDate !== flightData.returnDate) {
        const confirmed = window.confirm(
          `⚠️ Warning: Hotel check-out doesn't match flight departure!\n\n` +
          `Flight departure: ${flightData.returnDate}\n` +
          `Hotel check-out: ${newDate}\n\n` +
          `Continue with different dates?`
        );
        
        if (!confirmed) {
          return;
        }
      }
    }
    setCheckOut(newDate);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!city || !city.cityCode) {
      alert('Please select a city');
      return;
    }

    if (!checkIn) {
      alert('Please select check-in date');
      return;
    }

    if (!checkOut) {
      alert('Please select check-out date');
      return;
    }

    if (onSearch) {
      onSearch({
        cityCode: city.cityCode,
        checkIn: checkIn,      // Backend expects 'checkIn' not 'checkInDate'
        checkOut: checkOut,    // Backend expects 'checkOut' not 'checkOutDate'
        adults,
        rooms,
        ratings: '3,4,5',      // Add ratings parameter
        maxResults: 10
      });
    }
  };

  return (
    <form className="hotel-search-form" onSubmit={handleSubmit}>
      {/* Show info if auto-populated */}
      {autoPopulated && flightData && (
        <div className="auto-populate-notice">
          ℹ️ Auto-filled from your flight search. You can edit if needed.
        </div>
      )}

      {/* City Search with Autocomplete */}
      <div className="form-row">
        <div className="form-field form-field-full">
          <CitySearch
            label="City"
            placeholder="Search city..."
            value={city}
            onChange={handleCityChange}
            required
          />
        </div>
      </div>

      {/* Dates Row */}
      <div className="form-row dates-row">
        <div className="form-field">
          <label className="form-label">
            Check-in <span className="required">*</span>
          </label>
          <input
            type="date"
            className="form-input"
            value={checkIn}
            onChange={(e) => handleCheckInChange(e.target.value)}
            min={today}
            required
          />
        </div>

        <div className="form-field">
          <label className="form-label">
            Check-out <span className="required">*</span>
          </label>
          <input
            type="date"
            className="form-input"
            value={checkOut}
            onChange={(e) => handleCheckOutChange(e.target.value)}
            min={checkIn || today}
            required
          />
        </div>
      </div>

      {/* Guests and Rooms Row */}
      <div className="form-row options-row">
        <div className="form-field">
          <label className="form-label">
            Guests
          </label>
          <select
            className="form-select"
            value={adults}
            onChange={(e) => setAdults(parseInt(e.target.value))}
          >
            {[1, 2, 3, 4, 5, 6].map(num => (
              <option key={num} value={num}>
                {num} {num === 1 ? 'Guest' : 'Guests'}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label className="form-label">
            Rooms
          </label>
          <select
            className="form-select"
            value={rooms}
            onChange={(e) => setRooms(parseInt(e.target.value))}
          >
            {[1, 2, 3, 4].map(num => (
              <option key={num} value={num}>
                {num} {num === 1 ? 'Room' : 'Rooms'}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Search Button */}
      <div className="form-actions">
        <button
          type="submit"
          className="search-button"
          disabled={loading}
        >
          {loading ? (
            <>
              <div className="button-spinner"></div>
              Searching Hotels...
            </>
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Search Hotels
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default HotelSearchForm;